import { ElementRef, Injectable } from '@angular/core';
import { EventManager } from '@angular/platform-browser';

import { Observable, ReplaySubject, Subject, interval, skipWhile, take, tap } from 'rxjs';

import { isObject } from '../helpers';
import { PlatformService } from '../platform/platform.service';
import { WindowRefService } from '../window-ref/window-ref.service';

import { ScrollIntoViewAlignmentType, ScrollIntoViewBlock } from './scroll.types';

@Injectable({
  providedIn: 'root'
})
export class ScrollService {
  static readonly scrollDelay = 100;

  private readonly win: Window;
  private readonly isBrowser: boolean;
  private readonly scrollSubject = new Subject<number>();

  private pendingTargets: (ElementRef | HTMLElement)[] = [];
  private removeScrollHandler: Function;

  constructor(
    private readonly eventManager: EventManager,
    { nativeWindow }: WindowRefService,
    { isBrowser }: PlatformService
  ) {
    this.win = nativeWindow;
    this.isBrowser = isBrowser;
  }

  scrollTo(
    el: ElementRef | HTMLElement,
    alignment: ScrollIntoViewAlignmentType = ScrollIntoViewBlock.CENTER,
    delay = ScrollService.scrollDelay
  ): Observable<void> {
    const target = el instanceof ElementRef ? el.nativeElement : el;
    const scrollToSubject = new ReplaySubject<void>();

    if (this.isBrowser && target && !this.pendingTargets.includes(target)) {
      this.pendingTargets.push(target);
      setTimeout(() => {
        this.doScroll(target, alignment, scrollToSubject);
      }, delay);
    } else {
      this.resolve(scrollToSubject);
    }

    return scrollToSubject.asObservable();
  }

  get scroll$(): Observable<number> {
    if (!this.removeScrollHandler) {
      this.addScrollListener();
    }
    return this.scrollSubject.asObservable();
  }

  triggerScroll(): void {
    if (!this.scrollSubject.observed) {
      this.removeScrollListener();
    } else {
      this.scrollSubject.next(this.win.document.documentElement.scrollTop);
    }
  }

  private addScrollListener(): void {
    this.removeScrollHandler = this.eventManager.addEventListener(
      this.win as unknown as HTMLElement,
      'scroll',
      this.triggerScroll.bind(this)
    );
  }

  private removeScrollListener(): void {
    if (this.removeScrollHandler) {
      this.removeScrollHandler();
      this.removeScrollHandler = null;
    }
  }

  private doScroll(
    target: HTMLElement,
    alignment: ScrollIntoViewAlignmentType,
    scrollToSubject: ReplaySubject<void>
  ): void {
    const parentElement = target.parentElement;
    const { block, inline = null } = isObject(alignment) ? alignment : { block: alignment };

    const resolveAfter = (): void => {
      this.pendingTargets = this.pendingTargets.filter(tgt => tgt !== target);
      this.resolve(scrollToSubject);
    };

    target.scrollIntoView({
      behavior: 'smooth',
      block,
      ...(inline ? { inline } : {})
    } as ScrollIntoViewOptions);

    if (block === ScrollIntoViewBlock.START) {
      parentElement.scrollTop > 1
        ? interval(ScrollService.scrollDelay)
            .pipe(
              // wait for scrollTop to be reached or resolve after 15 iterations if not
              skipWhile(count => parentElement.scrollTop > 1 && count < 15),
              tap(() => (parentElement.scrollTop = 0)),
              take(1)
            )
            .subscribe(resolveAfter)
        : resolveAfter();
      return;
    }

    setTimeout(resolveAfter, ScrollService.scrollDelay);
  }

  private resolve(scrollToSubject: ReplaySubject<void>): void {
    scrollToSubject.next();
    scrollToSubject.complete();
  }
}
